import { format } from 'date-fns';
import { Button } from '../../../../core/components/atoms/Button';
import { Typography } from '../../../../core/components/molecules/Typography';
import { IPractices } from '../../model';
import { useNavigate } from 'react-router-dom';
import { useDeletePractice } from '../../hooks/deletePractice';
import { Fragment, useEffect, useState } from 'react';
import { Modal } from '../../../../core/components/templates/Modal';
import { Tag } from '../../../../core/components/atoms/Tag';
import { Icon } from '../../../../core/components/atoms/Icon';
import { Input } from '../../../../core/components/atoms/Input';
import Checkbox from '../../../../core/components/atoms/Checkbox';
import { useCreatePracticeRemake } from '../../hooks/createPracticeRemake';
import { useUpdatePractice } from '../../hooks/updatePractice';

interface IPracticeCard {
  data: IPractices;
  onRefresh?: () => void;
  selectionMode?: boolean;
  selected?: boolean;
  onToggleSelect?: (id: string) => void;
}

const translateInstitutionType: { [key: string]: string } = {
  both: 'Enem + Vestibular',
  enem: 'Enem',
  vestibular: 'Vestibular',
  inedit: 'Inédito',
}
export function PracticeCard({
  data,
  onRefresh,
  selectionMode = false,
  selected = false,
  onToggleSelect,
}: IPracticeCard) {
  const navigate = useNavigate();
  const {
    data: deletedData,
    mutate,
    isPending,
    isSuccess,
  } = useDeletePractice();
  const { mutate: createRemake, data: remakeData } = useCreatePracticeRemake();
  const {
    mutate: updateMutate,
    data: updatedData,
    isPending: isUpdatePending,
    isSuccess: isUpdateSuccess,
  } = useUpdatePractice();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isEditOpen, setIsEditOpen] = useState<boolean>(false);
  const [editTitle, setEditTitle] = useState<string>(data.title || '');

  const handleDelete = () => {
    mutate(data._id);
  };

  const handleEdit = () => {
    if (!editTitle.trim()) return;
    updateMutate({ id: data._id, title: editTitle.trim() });
  };

  useEffect(() => {
    if (isSuccess && deletedData.idDeleted) {
      setIsOpen(false);
      onRefresh?.();
    }
  }, [isPending]);

  useEffect(() => {
    if (isUpdateSuccess && updatedData?.ok) {
      setIsEditOpen(false);
      onRefresh?.();
    }
  }, [isUpdateSuccess]);

  useEffect(() => {
    if (remakeData && remakeData.simulatedExamId) {
      navigate(`/simulados/simulado/${remakeData.simulatedExamId}`);
    }
  }, [remakeData]);

  const redo = false;

  return (
    <div className='bg-color-lightest flex w-full rounded-md p-4 xs:flex-col xs:items-start xs:gap-xl lg:flex-row lg:items-center lg:justify-between'>
      <div className='flex flex-row items-center gap-lg'>
        {selectionMode && (
          <Checkbox
            variant='rounded'
            checked={selected}
            onChange={() => onToggleSelect?.(data._id)}
          />
        )}
        <div className='flex w-fit flex-col gap-sm'>
        <Typography size='small'>
          Criado em {format(new Date(data.createdAt), 'dd/MM/yyyy')}, às{' '}
          {format(new Date(data.createdAt), 'HH:mm')}
        </Typography>
        <Typography type='title' size='h4'>
          {data.title || 'Simulado'}
        </Typography>
        <div className='flex flex-row items-center gap-md'>
          {data?.simulationType && (
            <Tag variant='secondary' className='px-2'>
              {translateInstitutionType[data?.simulationType]}
            </Tag>
          )}
          {data?.institution && (
            <Tag variant='secondary' className='px-2'>{data.institution}</Tag>
          )}
          {data?.year?.length && data?.year.map((year: number) => 
            <Tag key={year} variant='secondary' className='px-2'>{year}</Tag>
          )}
          <Typography size='small'>
            {data.exercisesQuantity} Questões
          </Typography>
          {data.hasEssay && (
            <Typography size="small" className="text-mono-color-dark">
              Redação {data.essayScore && data.finished ? `Nota ${data.essayScore}` : ''}
            </Typography>
          )}
          {data.finished && (
            <Typography size='small'>
              <span className='xs:hidden lg:block'>
                {data.percentage || 0}% de aproveitamento
              </span>
              <span className='xs:block lg:hidden'>
                {data.percentage || 0}%
              </span>
            </Typography>
          )}
          </div>
        </div>
      </div>
      <div className='flex flex-row gap-lg xs:w-full lg:w-fit'>
        {data.finished ? (
          <Fragment>
            {redo && (
              <Button
                variant='outlined'
                className='h-fit rounded-md'
                onClick={() => createRemake(data._id)}
              >
                <span className='xs:hidden lg:block'>Refazer simulado</span>
                <span className='xs:block lg:hidden'>Refazer</span>
              </Button>
            )}
            <Button
              variant='outlined'
              className='h-fit rounded-md'
              onClick={() => navigate(`/simulados/feedback/${data._id}`)}
            >
              <span className='xs:hidden lg:block'>Ver gabarito</span>
              <span className='xs:block lg:hidden'>Gabarito</span>
            </Button>
            <Button
              variant='text'
              iconType='material'
              LeftIcon='edit'
              iconVariant='rounded'
              onlyIcon
              className='xs:block lg:hidden'
              onClick={() => {
                setEditTitle(data.title || '');
                setIsEditOpen(true);
              }}
            />
            <Button
              variant='text'
              iconType='material'
              LeftIcon='delete'
              iconVariant='rounded'
              onlyIcon
              className='text-red-500 xs:block lg:hidden'
              onClick={() => setIsOpen(true)}
            />
          </Fragment>
        ) : (
          <Button
            className='h-fit justify-center rounded-md xs:w-full lg:w-fit'
            onClick={() => navigate(`/simulados/simulado/${data._id}`)}
          >
            <Icon Icon='refresh' type='material' />
            Continuar simulado
          </Button>
        )}
        <Button
          variant='outlined'
          iconType='material'
          LeftIcon='edit'
          onlyIcon
          className='py-0 pt-2 xs:hidden lg:block'
          onClick={() => {
            setEditTitle(data.title || '');
            setIsEditOpen(true);
          }}
        />
        <Button
          variant='outlined'
          iconType='material'
          LeftIcon='delete'
          onlyIcon
          className='text-feedback-color-error-pure py-0 pt-2 xs:hidden lg:block'
          onClick={() => setIsOpen(true)}
        />
      </div>
      <Modal
        isOpen={isOpen}
        onClose={() => setIsOpen(false)}
        position='center'
        className='p-0 xs:w-full lg:w-2/5'
      >
        <div className='flex flex-col gap-xl'>
          <div className='flex flex-col gap-sm'>
            <Typography weight='bold'>
              Tem certeza que deseja excluir esse simulado?
            </Typography>
            <Typography>Não será possível recuperar o simulado.</Typography>
          </div>
          <div className='flex flex-row items-center justify-end gap-sm'>
            <Button variant='text' onClick={() => setIsOpen(false)}>
              Cancelar
            </Button>
            <Button variant='text' onClick={() => handleDelete()}>
              Excluir simulado
            </Button>
          </div>
        </div>
      </Modal>
      <Modal
        isOpen={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        position='center'
        className='p-0 xs:w-full lg:w-2/5'
      >
        <div className='flex flex-col gap-xl'>
          <Typography weight='bold'>Editar simulado</Typography>
          <Input
            label='Nome do Simulado'
            value={editTitle}
            onChange={(e) => setEditTitle(e.target.value)}
            maxLength={100}
          />
          <div className='flex flex-row items-center justify-end gap-sm'>
            <Button variant='text' onClick={() => setIsEditOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant='text'
              onClick={() => handleEdit()}
              disabled={isUpdatePending || !editTitle.trim()}
            >
              Salvar
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
